import { arrayRemove, arrayUnion, collection, deleteDoc, doc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore"

export default class FirebasePokemons {
  constructor(baseDeDonnees) {
    this.baseDeDonnees = baseDeDonnees
  }

  get bdd() {
    return this.baseDeDonnees.bdd
  }

  recupererIdUtilisateurCourant() {
    return this.baseDeDonnees.recupererIdUtilisateurCourant()
  }

  async journaliserErreurEtRelancer(error, contexte) {
    return this.baseDeDonnees.journaliserErreurEtRelancer(error, contexte)
  }

  async ajouterUnPokemon(pokemon) {
    try {
      const dataPokemon = {
        membre: this.recupererIdUtilisateurCourant(),
        pokemon: {
          nom: pokemon.nom,
          commentaire: pokemon.commentaire,
          specificites: pokemon.specificites,
          id: pokemon.id,
          creerA: serverTimestamp(),
          dernierModification: serverTimestamp(),
        },
      }

      if (Array.isArray(pokemon.communautes) && pokemon.communautes.length > 0) {
        dataPokemon.communautes = pokemon.communautes
      } else if (pokemon.communauteId) {
        dataPokemon.communautes = [pokemon.communauteId]
      }

      return setDoc(doc(this.bdd, "pokemons", pokemon.id), dataPokemon)
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "ajouterUnPokemon")
    }
  }

  async modifierUnPokemon(pokemon) {
    try {
      const docRefPokemon = doc(this.bdd, "pokemons", pokemon.id)

      if (docRefPokemon) {
        await updateDoc(docRefPokemon, {
          pokemon: {
            nom: pokemon.nom,
            commentaire: pokemon.commentaire,
            specificites: pokemon.specificites,
            derniereModification: serverTimestamp(),
          },
        })
      }
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "modifierUnPokemon")
    }
  }

  async recupererLesPokemons() {
    try {
      const snapshot = await getDocs(collection(this.bdd, "pokemons"))
      const pokemons = []

      snapshot.forEach((docSnapshot) => {
        pokemons.push(docSnapshot.data())
      })
      return pokemons
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "recupererLesPokemons")
    }
  }

  async supprimerUnPokemon(idPokemon) {
    try {
      const docRefPokemon = doc(this.bdd, "pokemons", idPokemon)

      if (docRefPokemon) {
        await deleteDoc(docRefPokemon)
      }
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "supprimerUnPokemon")
    }
  }

  async recupererLesPokemonsDuMembreConnecte(idMembre) {
    try {
      const q = query(collection(this.bdd, "pokemons"), where("membre", "==", idMembre))
      const snapshot = await getDocs(q)
      const pokemons = []

      snapshot.forEach((docSnapshot) => {
        const pokemonData = docSnapshot.data().pokemon
        pokemonData.id = docSnapshot.id
        pokemons.push(pokemonData)
      })
      return pokemons
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "recupererLesPokemonsDuMembreConnecte")
    }
  }

  async supprimerPokemons(idMembre) {
    try {
      const q = query(collection(this.bdd, "pokemons"), where("membre", "==", idMembre))
      const snapshot = await getDocs(q)

      await Promise.all(snapshot.docs.map((docSnapshot) => deleteDoc(docSnapshot.ref)))
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "supprimerPokemons")
    }
  }

  async recupererLesPokemonsDesAutresMembresDeCeluiConnecte() {
    try {
      const idMembreCourant = this.recupererIdUtilisateurCourant()
      const q = query(collection(this.bdd, "pokemons"), where("membre", "!=", idMembreCourant))
      const snapshot = await getDocs(q)
      const pokemons = []
      snapshot.forEach((docSnapshot) => {
        pokemons.push(docSnapshot.data())
      })

      const membres = await this.baseDeDonnees.membres.recupererLesMembres()
      const pokemonDesAutresMembres = pokemons.map((pokemon) => ({
        membre: membres[pokemon.membre].pseudo,
        id: pokemon.membre,
        echanges: [pokemon.pokemon],
        commentaire: membres[idMembreCourant]?.commentaires?.[pokemon.membre],
      }))

      return pokemonDesAutresMembres.reduce((pokemonsParMembre, pokemonActuel) => {
        const membreExistant = pokemonsParMembre.find((pokemon) => pokemon.id === pokemonActuel.id)
        if (membreExistant) {
          membreExistant.echanges.push(...pokemonActuel.echanges)
        } else {
          pokemonsParMembre.push(pokemonActuel)
        }
        return pokemonsParMembre
      }, [])
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "recupererLesPokemonsDesAutresMembresDeCeluiConnecte")
    }
  }

  async recupererLesPokemonsDesAutresMembresDeCeluiConnectePourUneCommunaute(idCommunaute) {
    try {
      const idMembreCourant = this.recupererIdUtilisateurCourant()
      const qMembre = query(collection(this.bdd, "communautes", idCommunaute, "membres"))
      const snapshotMembre = await getDocs(qMembre)
      const membresCommunaute = []
      snapshotMembre.forEach((docSnapshot) => {
        membresCommunaute.push(docSnapshot.id)
      })

      const pokemons = []
      await Promise.all(
        membresCommunaute.map(async (idMembre) => {
          const qPokemon = query(collection(this.bdd, "pokemons"), where("membre", "==", idMembre))
          const snapshotPokemon = await getDocs(qPokemon)
          snapshotPokemon.forEach((docSnapshot) => {
            pokemons.push(docSnapshot.data())
          })
        }),
      )

      const membres = await this.baseDeDonnees.membres.recupererLesMembres()
      const pokemonDesAutresMembres = pokemons
        .map((pokemon) => {
          if (pokemon.membre === idMembreCourant) {
            return null
          }
          return {
            membre: membres[pokemon.membre].pseudo,
            id: pokemon.membre,
            echanges: [pokemon.pokemon],
            commentaire: membres[idMembreCourant]?.commentaires?.[pokemon.membre],
          }
        })
        .filter((pokemon) => pokemon !== null)

      return pokemonDesAutresMembres.reduce((pokemonsParMembre, pokemonActuel) => {
        const membreExistant = pokemonsParMembre.find((pokemon) => pokemon.id === pokemonActuel.id)
        if (membreExistant) {
          membreExistant.echanges.push(...pokemonActuel.echanges)
        } else {
          pokemonsParMembre.push(pokemonActuel)
        }
        return pokemonsParMembre
      }, [])
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "recupererLesPokemonsDesAutresMembresDeCeluiConnectePourUneCommunaute")
    }
  }

  async ajouterCommunauteAuxPokemonsDuMembre(idMembre, idCommunaute) {
    try {
      const q = query(collection(this.bdd, "pokemons"), where("membre", "==", idMembre))
      const snapshot = await getDocs(q)

      await Promise.all(snapshot.docs.map((docSnapshot) => updateDoc(docSnapshot.ref, { communautes: arrayUnion(idCommunaute) })))
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "ajouterCommunauteAuxPokemonsDuMembre")
    }
  }

  async supprimerCommunauteDesPokemonsDuMembre(idMembre, idCommunaute) {
    try {
      const q = query(collection(this.bdd, "pokemons"), where("membre", "==", idMembre))
      const snapshot = await getDocs(q)

      await Promise.all(snapshot.docs.map((docSnapshot) => updateDoc(docSnapshot.ref, { communautes: arrayRemove(idCommunaute) })))
    } catch (error) {
      await this.journaliserErreurEtRelancer(error, "supprimerCommunauteDesPokemonsDuMembre")
    }
  }
}
